import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Form,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import { TagManager } from "components/general/tags";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import useTasks from "hooks/tasks/useTasks";
import { useFileUpload } from "hooks/useFileUpload";
import AttachmentsSection from "../components/AttachmentsSection";
import useProcessedDescription from "hooks/useProcessedDescription";

dayjs.extend(relativeTime);

const STATUS_CONFIG = {
  OPEN: { bg: "primary", label: "Open" },
  IN_PROGRESS: { bg: "warning", label: "In Progress" },
  COMPLETED: { bg: "success", label: "Completed" },
  BLOCKED: { bg: "danger", label: "Blocked" },
};

const PRIORITY_MAP = {
  0: { label: "None", bg: "secondary" },
  1: { label: "Low", bg: "info" },
  2: { label: "Medium", bg: "warning" },
  3: { label: "High", bg: "danger" },
  4: { label: "Critical", bg: "danger" },
};

const TaskDetailPage = () => {
  const navigate = useNavigate();
  const { projectId, iterationId, taskId } = useParams();
  const { user } = useAuth();
  const {
    fetchTaskComments,
    addTaskComment,
    updateTaskComment,
    deleteTaskComment,
    fetchTaskLinks,
    addTaskLink,
    deleteTaskLink,
    fetchTaskAttachments,
    addTaskAttachments,
    deleteTaskAttachment,
    getTaskAttachment,
    getTaskById,
  } = useTasks();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [links, setLinks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [linksLoading, setLinksLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const processedDescription = useProcessedDescription(task?.description, task?.textFiles);

  // File upload hook
  const { uploadFile, uploading } = useFileUpload();

  // Comment form
  const [newComment, setNewComment] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  // Delete modals
  const [deleteCommentModal, setDeleteCommentModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const userName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      user.username
    : "";

  const loadComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const data = await fetchTaskComments(projectId, taskId);
      setComments(data);
    } catch (err) {
      console.error("Failed to load comments:", err);
      toast.error("Failed to load comments");
    } finally {
      setCommentsLoading(false);
    }
  }, [projectId, taskId, fetchTaskComments]);

  // Load links
  const loadLinks = useCallback(async () => {
    try {
      setLinksLoading(true);
      const data = await fetchTaskLinks(taskId);
      setLinks(data);
    } catch (err) {
      console.error("Failed to load links:", err);
      toast.error("Failed to load links");
    } finally {
      setLinksLoading(false);
    }
  }, [taskId, fetchTaskLinks]);

  // Load attachments
  const loadAttachments = useCallback(async () => {
    try {
      setAttachmentsLoading(true);
      const data = await fetchTaskAttachments(projectId, taskId);
      console.log("[loadAttachments] raw data:", data);
      // Normalize attachment objects so the UI always has `id`, `fileId`, `url`, and `name`.
      const normalized = (Array.isArray(data) ? data : []).map((a) => {
        const url = a.fileUrl || a.url || a.file_url || a.meta?.url || null;

        // Try many possible fields/backends for the original filename
        let name =
          a.label ||
          a.fileName ||
          a.file_name ||
          a.filename ||
          a.name ||
          a.file?.filename ||
          a.file?.name ||
          a.meta?.filename ||
          a.meta?.originalFilename ||
          null;

        // If still missing, try to derive from URL path
        if (!name && url) {
          try {
            const path = url.split("?")[0];
            const parts = path.split("/");
            name = decodeURIComponent(parts[parts.length - 1] || "");
          } catch (e) {
            name = null;
          }
        }

        // derive a simple mime-like type from extension if missing
        let type = a.type || a.mimeType || null;
        if (!type && name) {
          const ext = (name.match(/\.([a-z0-9]+)$/i) || [])[1];
          if (ext) {
            if (/(jpg|jpeg|png|gif|webp|bmp|svg)/i.test(ext))
              type = `image/${ext === "jpg" ? "jpeg" : ext}`;
            else if (/(pdf)/i.test(ext)) type = "application/pdf";
          }
        }

        // also expose fileExtension explicitly when available
        const extensionFromFields =
          a.fileExtension ||
          (name && (name.match(/\.([a-z0-9]+)$/i) || [])[0]) ||
          null;

        return {
          id: a.id,
          fileId: a.fileId || a.file_id || null,
          url,
          name,
          fileExtension: extensionFromFields,
          type,
          createdAt: a.createdAt || a.created_at || null,
          createdBy: a.createdBy || a.created_by || null,
          ...a,
        };
      });
      console.log("[loadAttachments] normalized:", normalized);
      setAttachments(normalized);
    } catch (err) {
      console.error("Failed to load attachments:", err);
      toast.error("Failed to load attachments");
    } finally {
      setAttachmentsLoading(false);
    }
  }, [projectId, taskId, fetchTaskAttachments]);

  useEffect(() => {
    if (projectId && taskId) {
      loadComments();
      loadLinks();
      loadAttachments();
    }
  }, [projectId, taskId, loadComments, loadLinks, loadAttachments]);

  useEffect(() => {
    if (!taskId) {
      setTask(null);
      setLoading(false);
      return;
    }

    const loadTaskDetails = async () => {
      setLoading(true);
      try {
        const data = await getTaskById(taskId);
        console.info("[TaskDetailPage] loaded task", {
          taskId,
          descriptionLength: data?.description?.length || 0,
        });
        setTask(data);
      } catch (err) {
        console.error("[TaskDetailPage] failed to load task", err);
        toast.error(err.message || "Failed to load task details");
      } finally {
        setLoading(false);
      }
    };

    loadTaskDetails();
  }, [taskId, getTaskById]);

  // ─── Comment handlers ───────────────────────────────────────────────
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      setCommentSubmitting(true);
      const created = await addTaskComment(projectId, taskId, {
        content: newComment,
      });
      setComments((prev) => [created, ...prev]);
      setNewComment("");
      toast.success("Comment added");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add comment");
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editCommentContent.trim()) return;
    try {
      const updated = await updateTaskComment(projectId, taskId, commentId, {
        content: editCommentContent,
      });
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c)),
      );
      setEditingCommentId(null);
      setEditCommentContent("");
      toast.success("Comment updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update comment");
    }
  };

  const handleAddFile = (fileData) => {
    // Normalize incoming fileData then add to attachments
    const normalized = {
      id: fileData.id,
      fileId: fileData.fileId || fileData.file_id || null,
      url: fileData.url || fileData.meta?.url || null,
      name: fileData.name || fileData.fileName || fileData.label || null,
      ...fileData,
    };
    console.log("[handleAddFile] incoming fileData:", fileData, "normalized:", normalized);
    setAttachments((prev) => [...prev, normalized]);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteTaskComment(projectId, taskId, commentToDelete);
      setComments((prev) => prev.filter((c) => c.id !== commentToDelete));
      toast.success("Comment deleted");
    } catch (err) {
      toast.error("Failed to delete comment");
    } finally {
      setDeleteLoading(false);
      setDeleteCommentModal(false);
      setCommentToDelete(null);
    }
  };

  // ─── Attachment handlers ────────────────────────────────────────────
  const handleAddLink = async (linkData) => {
    try {
      const created = await addTaskLink(taskId, {
        url: linkData.url,
        label: linkData.label,
      });
      const newLinks = Array.isArray(created) ? created : [created];
      setLinks((prev) => [...prev, ...newLinks]);
      toast.success("Link added successfully");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add link");
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const uploadResult = await uploadFile(user?.id, file, {
        purpose: "USER_PROFILE",
        fileType: "DOCUMENT",
      });
      console.log("[handleFileUpload] uploadResult:", uploadResult);

      // derive extension from filename (preserve original extension if present)
      const extMatch = (file.name || "").match(/\.([a-z0-9]+)$/i);
      const fileExtension = extMatch ? `.${extMatch[1]}` : "";

      const attachmentData = [
        {
          fileId: uploadResult.fileId,
          fileName: file.name,
          fileExtension,
          fileSize: file.size,
          mimeType: file.type,
          url: uploadResult.meta?.url,
        },
      ];

      console.log("[handleFileUpload] attachmentData to send:", attachmentData);
      const savedAttachments = await addTaskAttachments(
        projectId,
        taskId,
        attachmentData,
      );
      console.log("[handleFileUpload] savedAttachments:", savedAttachments);

      // Return the first saved attachment – this will be passed to onAddFile by AttachmentsSection
      toast.success("File uploaded successfully");
      return savedAttachments[0];
    } catch (err) {
      console.error("❌ File upload error:", err);
      toast.error(err.message || "Failed to upload file");
      throw err;
    }
  };

  const handleDeleteAttachment = async (id, type, opts = {}) => {
    try {
      setDeleteLoading(true);

      if (type === "link") {
        await deleteTaskLink(taskId, id);
        setLinks((prev) => prev.filter((l) => l.id !== id));
        toast.success("Link deleted");
        return;
      }

      if (type === "file") {
        try {
          await deleteTaskAttachment(projectId, taskId, id);
        } catch (err) {
          const fileId = opts?.fileId;
          if (fileId && fileId !== id) {
            try {
              await deleteTaskAttachment(projectId, taskId, fileId);
              setAttachments((prev) => prev.filter((a) => a.id !== id && a.fileId !== fileId));
              toast.success("File deleted");
              return;
            } catch (err2) {
              console.error("Fallback delete by fileId failed", err2);
              throw err2;
            }
          }
          throw err;
        }

        setAttachments((prev) => prev.filter((a) => a.id !== id));
        toast.success("File deleted");
        return;
      }
    } catch (err) {
      toast.error(`Failed to delete ${type}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Tag change handler ─────────────────────────────────────────────
  const handleTagsChange = (newTags) => {
    setTask((prev) => (prev ? { ...prev, tags: newTags } : prev));
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading task...</span>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-5">
        <h5 className="text-500">Task not found</h5>
        <Button variant="falcon-primary" size="sm" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[task.status] || {};
  const priorityCfg = PRIORITY_MAP[task.priority] || PRIORITY_MAP[0];

  return (
    <>
      <WidgetSectionTitle
        icon="clipboard-check"
        title={task.title}
        subtitle={`Task in project`}
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      {/* Breadcrumb */}
      <div className="mb-3">
        <small className="text-500">
          <span
            className="text-primary cursor-pointer"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(paths.projects)}
          >
            Projects
          </span>
          {" / "}
          <span
            className="text-primary"
            style={{ cursor: "pointer" }}
            onClick={() => navigate(paths.projectDetails(projectId))}
          >
            Project
          </span>
          {task.iterationId && iterationId && iterationId !== "_" && (
            <>
              {" / "}
              <span
                className="text-primary"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    paths.projectIterationDetails(projectId, iterationId),
                  )
                }
              >
                Iteration
              </span>
            </>
          )}
          {" / "}
          <span className="text-700">{task.title}</span>
        </small>
      </div>

      <Row className="g-3">
        {/* Main Content */}
        <Col lg={8}>
          {/* Task Info Card */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Task Details</h6>
              <div>
                <SubtleBadge bg={statusCfg.bg} className="me-2">
                  {statusCfg.label || task.status}
                </SubtleBadge>
                <SubtleBadge bg={priorityCfg.bg}>
                  P{task.priority ?? "—"}: {priorityCfg.label}
                </SubtleBadge>
              </div>
            </Card.Header>
            <Card.Body>
              {task.description && (
              <div className="mb-3">
                <h6 className="text-600 fs-10">Description</h6>
                <div
                  className="text-800 fs-10"
                  dangerouslySetInnerHTML={{ __html: processedDescription }}
                />
              </div>
            )}

              {task.acceptanceCriteria && (
                <div className="mb-3">
                  <h6 className="text-600 fs-10">Acceptance Criteria</h6>
                  <div
                    className="text-800 fs-10"
                    dangerouslySetInnerHTML={{
                      __html: task.acceptanceCriteria,
                    }}
                  />
                </div>
              )}

              <Row className="g-2 mt-2">
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Points</small>
                  <span className="fw-semi-bold">{task.points}</span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Assignee</small>
                  <span className="fw-semi-bold">
                    {task.assigneeName || "Unknown"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Created By</small>
                  <span className="fw-semi-bold">
                    {task.createdByName || "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Started At</small>
                  <span>
                    {task.startedAt
                      ? dayjs(task.startedAt).format("MMM D, YYYY")
                      : "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">
                    Expected Completion
                  </small>
                  <span>
                    {task.expectedCompletionDate
                      ? dayjs(task.expectedCompletionDate).format("MMM D, YYYY")
                      : "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Actual Completion</small>
                  <span>
                    {task.actualCompletionDate
                      ? dayjs(task.actualCompletionDate).format("MMM D, YYYY")
                      : "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Created</small>
                  <span>
                    {task.createdAt
                      ? dayjs(task.createdAt).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </span>
                </Col>
                <Col sm={6} md={4}>
                  <small className="text-500 d-block">Updated</small>
                  <span>
                    {task.updatedAt
                      ? dayjs(task.updatedAt).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </span>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Comments Section */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="comments" className="me-2" />
                Comments ({comments.length})
              </h6>
            </Card.Header>
            <Card.Body>
              {/* Add Comment Form */}
              <Form onSubmit={handleAddComment} className="mb-3">
                <InputGroup>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    maxLength={8000}
                  />
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!newComment.trim() || commentSubmitting}
                  >
                    {commentSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon="paper-plane" />
                    )}
                  </Button>
                </InputGroup>
              </Form>

              {commentsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : comments.length === 0 ? (
                <p className="text-500 text-center mb-0">No comments yet</p>
              ) : (
                <ListGroup variant="flush">
                  {comments.map((comment) => (
                    <ListGroup.Item key={comment.id} className="px-0">
                      {editingCommentId === comment.id ? (
                        <div>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={editCommentContent}
                            onChange={(e) =>
                              setEditCommentContent(e.target.value)
                            }
                            maxLength={8000}
                          />
                          <div className="mt-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleUpdateComment(comment.id)}
                              className="me-2"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="falcon-default"
                              onClick={() => setEditingCommentId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="fs-10">
                                {comment.createdByName || "Unknown"}
                              </strong>
                              <small className="text-500 ms-2">
                                {dayjs(comment.createdAt).fromNow()}
                              </small>
                            </div>
                            <div>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-500 p-0 me-2"
                                onClick={() => {
                                  setEditingCommentId(comment.id);
                                  setEditCommentContent(comment.content);
                                }}
                              >
                                <FontAwesomeIcon icon="edit" />
                              </Button>
                              <Button
                                size="sm"
                                variant="link"
                                className="text-danger p-0"
                                onClick={() => {
                                  setCommentToDelete(comment.id);
                                  setDeleteCommentModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon="trash-alt" />
                              </Button>
                            </div>
                          </div>
                          <p className="mb-0 mt-1 fs-10 text-800">
                            {comment.content}
                          </p>
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Attachments Section */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="paperclip" className="me-2" />
                Attachments ({attachments.length + links.length})
              </h6>
            </Card.Header>
            <Card.Body>
              {attachmentsLoading || linksLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <AttachmentsSection
                  links={links}
                  files={attachments}
                  onAddLink={handleAddLink}
                  onAddFile={handleAddFile} // now properly defined
                  onDelete={handleDeleteAttachment}
                  onFileUpload={handleFileUpload}
                  onGetAttachment={getTaskAttachment ? (attachmentId) => getTaskAttachment(projectId, taskId, attachmentId) : undefined}
                />
              )}
            </Card.Body>
          </Card>

          {/* Tags */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="tags" className="me-2" />
                Tags
              </h6>
            </Card.Header>
            <Card.Body>
              <TagManager
                entityType="task"
                entityId={taskId}
                projectId={projectId}
                currentTags={task.tags || []}
                onTagsChange={handleTagsChange}
              />
            </Card.Body>
          </Card>

          {/* Actions */}
          <Card>
            <Card.Body>
              <Button
                variant="falcon-primary"
                size="sm"
                className="w-100 mb-2"
                onClick={() =>
                  navigate(
                    paths.projectTaskEdit(
                      projectId,
                      iterationId || "_",
                      taskId,
                    ),
                  )
                }
              >
                <FontAwesomeIcon icon="edit" className="me-1" />
                Edit Task
              </Button>
              <Button
                variant="falcon-default"
                size="sm"
                className="w-100"
                onClick={() => navigate(-1)}
              >
                <FontAwesomeIcon icon="arrow-left" className="me-1" />
                Go Back
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Modals */}
      <ConfirmDeleteModal
        show={deleteCommentModal}
        setShow={setDeleteCommentModal}
        onConfirm={handleDeleteComment}
        itemName="comment"
        loading={deleteLoading}
      />
    </>
  );
};

export default TaskDetailPage;
